/**
 * Normalizes and validates a logo URL for use in an `<img src>`. Shared by
 * both the React (`EntityIcon`) and HTML-string (`getEntityIconHTML`) paths
 * so both apply the same scheme allowlist. Returns `null` when the input is
 * empty or looks unsafe — callers fall back to the letter-chip variant.
 *
 * Accepted forms:
 *  - `https?://...`                                       → returned as-is
 *  - `/foo`, `/foo/bar.png` (same-origin absolute paths)  → returned as-is
 *  - Legacy bare relative paths like `images/adidas.png` or
 *    `public/images/adidas.png` — normalized to `/images/adidas.png`.
 *    The pre-migration ProfileChart contained a `getIconPath` helper that
 *    did this rewrite, so any API/data source still shipping bare paths
 *    continues to render instead of silently falling to a letter chip.
 *
 * Rejected forms (return `null`):
 *  - Any URL scheme other than http/https (including `javascript:`,
 *    `data:`, `vbscript:` etc.). Detected by a `:` before the first `/`.
 *  - Protocol-relative URLs (`//evil.com/x.png`)
 *  - Backslash-disguised protocol-relative URLs (`/\evil.com/x.png`,
 *    `\\evil.com/x.png`)
 */
export function resolveLogoUrl(input: string | null | undefined): string | null {
  if (!input) return null;
  const trimmed = input.trim();
  if (!trimmed) return null;

  if (/^https?:\/\//i.test(trimmed)) return trimmed;

  // Reject anything that already looks like a scheme (javascript:, data:,
  // vbscript:, file:, etc.) — a `:` before the first `/` is the signal.
  const firstSlash = trimmed.indexOf("/");
  const firstColon = trimmed.indexOf(":");
  if (firstColon !== -1 && (firstSlash === -1 || firstColon < firstSlash)) {
    return null;
  }

  // Reject protocol-relative and backslash-disguised protocol-relative URLs.
  if (trimmed.startsWith("//") || trimmed.startsWith("\\\\")) return null;
  if (trimmed.startsWith("/\\") || trimmed.startsWith("\\/")) return null;

  // Same-origin absolute path — allowed as-is.
  if (/^\/[^/\\]/.test(trimmed)) return trimmed;

  // Legacy bare relative path — normalize `public/foo` → `/foo`, otherwise
  // prepend `/`. Backslashes have already been rejected above, so this only
  // fires for simple `images/foo.png` style inputs.
  if (/^[A-Za-z0-9._-]/.test(trimmed)) {
    const stripped = trimmed.startsWith("public/") ? trimmed.slice("public/".length) : trimmed;
    return `/${stripped}`;
  }

  return null;
}
